import i18n from '@i18n';


// #region FIELD DEFINITIONS
export const exifFieldDefinitions = [
  { name: 'Camera', key: 'camera', aria: () => i18n.EXIF.CAMERA_MODEL, icon: 'camera', settingKey: 'camera' },
  { name: 'Lens', key: 'lens', aria: () => i18n.EXIF.LENS, icon: 'lens', settingKey: 'lens' },
  { name: 'FocalLength', key: 'focal_length', aria: () => i18n.EXIF.FOCAL_LENGTH, icon: 'eye', settingKey: 'focal_length' },
  { name: 'ShutterSpeed', key: 'shutter_speed', aria: () => i18n.EXIF.SHUTTER_SPEED, icon: 'shutter_speed', settingKey: 'shutter_speed' },
  { name: 'Aperture', key: 'aperture', aria: () => i18n.EXIF.APERTURE, icon: 'aperture', settingKey: 'aperture' },
  { name: 'Iso', key: 'iso', aria: () => i18n.EXIF.ISO, icon: 'iso', settingKey: 'iso' },
  { name: 'Date', key: 'date', aria: () => i18n.EXIF.DATE, icon: 'calendar', settingKey: 'date', checkZero: true, altSettingKey: 'time' },
  { name: 'Keywords', key: 'keywords', aria: () => i18n.EXIF.KEYWORDS, icon: 'keywords', settingKey: 'keywords' },
  { name: 'Copyright', key: 'copyright', aria: () => i18n.EXIF.COPYRIGHT, icon: 'copyright', settingKey: 'copyright' },
];

export const metaFieldDefinitions = [
  { name: 'Author', key: 'author', aria: () => i18n.EXIF.AUTHOR, icon: 'user', settingKey: 'author' },
];

//#region VALUE GETTERS 

// Get EXIF value from image.exif object
export const getExifValue = (image, key) => image?.exif?.[key] || "N/A";

// Get meta value from image.meta object
export const getMetaValue = (image, key) => image?.meta?.[key] || "N/A";

// Check if a field value should be visible based on settings
const isFieldVisible = (value, field, settings) => {
  if (value === "N/A") return false;
  if (field.checkZero && value == 0) return false;
  if (field.altSettingKey) {
    return settings[field.settingKey] || settings[field.altSettingKey];
  }
  return settings[field.settingKey];
};


//#region DATA TRANSFORMATION

// Transform raw mwl_data from PHP into structured image objects
export const transformMwlData = (mwlData) => {
  if (!mwlData) return [];
  
  return Object.values(mwlData)
    .filter(item => item.data)
    .map(item => ({
      id: item.data.id,
      src: item.file,
      srcset: item.srcset || '',
      msrc: item.file,
      width: item.dimension.width,
      height: item.dimension.height,
      thumbCropped: true,
      meta: {
        title: item.data.title,
        caption: item.data.caption,
        description: item.data.description,
        keywords: item.data.keywords,
        author: item.data.author,
        date: item.data.date,
        download: item.download_link,
      },
      exif: {
        camera: item.data.camera,
        focal_length: item.data.focal_length,
        shutter_speed: item.data.shutter_speed,
        aperture: item.data.aperture,
        iso: item.data.iso,
        lens: item.data.lens,
        date: item.data.date,
        keywords: item.data.keywords,
        gps: item.data.gps,
        copyright: item.data.copyright,
      },
    }));
};

//#region EXIF LIST BUILDERS

// Build a list of EXIF items for display (filters out hidden items)
export const buildExifList = (image, settings) => {
  const list = [];

  // Process EXIF fields
  exifFieldDefinitions.forEach(field => {
    const value = getExifValue(image, field.key);
    list.push({
      name: field.name,
      aria: field.aria(),
      icon: field.icon,
      value,
      visible: isFieldVisible(value, field, settings),
    });
  });

  // Process meta fields
  metaFieldDefinitions.forEach(field => {
    const value = getMetaValue(image, field.key);
    list.push({
      name: field.name,
      aria: field.aria(),
      icon: field.icon,
      value,
      visible: isFieldVisible(value, field, settings),
    });
  });

  return list;
};

// Build visible-only EXIF list
export const buildVisibleExifList = (image, settings) => {
  return buildExifList(image, settings).filter(item => item.visible);
};
